/**
 * ═══════════════════════════════════════════════════════════════════════════
 * G-72 — what the console said, kept so a bug report can carry it
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * "It didn't work" is not actionable. "It didn't work, and here is the failed
 * request and the stack" is. This keeps a bounded tail of console output and
 * uncaught errors so a florist's report arrives with evidence attached.
 *
 * BOUNDED ON PURPOSE, twice over. A render loop can emit thousands of identical
 * lines a second; without a cap this would eat the tablet's memory before
 * anyone noticed, and then post megabytes of it over a shop's connection.
 *
 * REDACTED BEFORE IT IS EVER STORED, not on the way out. The scrubbing happens
 * as each line is captured, so a token never sits in the tablet's memory in the
 * first place — and there is no second path that could forget to scrub.
 */

/** Lines kept. Enough to see what led to a failure, short enough to read. */
const MAX_LINES = 200;
/** One runaway line must not become the whole report. */
const MAX_LINE_CHARS = 2000;

export type ConsoleLevel = "log" | "info" | "warn" | "error";

export interface ConsoleEntry {
  at: string;
  level: ConsoleLevel;
  text: string;
}

const buffer: ConsoleEntry[] = [];
let installed = false;

/**
 * Strings that must never leave the tablet.
 *
 * The auth plane runs on 15-minute JWTs and an 8-digit floor PIN, and both turn
 * up in console output the moment anyone debugs a request. A bug report is a
 * channel OUT of the shop, so it is exactly the wrong place for either.
 *
 * Deliberately blunt: over-redacting costs a `[redacted]` in a log line, while
 * under-redacting puts a working credential in a database row that a pilot
 * leaves lying around for ninety days.
 */
const REDACTIONS: Array<{ pattern: RegExp; replace: string }> = [
  // A JWT, anywhere — three base64url segments. Catches Authorization headers,
  // logged request bodies and anything that stringified a token by accident.
  {
    pattern: /eyJ[A-Za-z0-9_-]{6,}\.[A-Za-z0-9_-]{6,}\.[A-Za-z0-9_-]{6,}/g,
    replace: "[redacted-token]",
  },
  // `Bearer <anything>` even when the token is not a JWT.
  { pattern: /Bearer\s+[A-Za-z0-9._~+/=-]{8,}/gi, replace: "Bearer [redacted]" },
  // A labelled secret in JSON or a query string: token=…, password: "…", pin=…
  {
    pattern:
      /("?\b(?:pin|password|passwd|secret|token|apiKey|api_key|authorization|csrf|refreshToken)\b"?\s*[:=]\s*)("[^"]*"|'[^']*'|[^\s,&}]+)/gi,
    replace: "$1[redacted]",
  },
  // The floor PIN is exactly 8 digits. A bare 8-digit run is far more likely to
  // be a credential than anything a florist needs in a bug report.
  { pattern: /\b\d{8}\b/g, replace: "[redacted-pin]" },
];

/** Exported so the rule is provable without a console or a network. */
export function redactSensitive(text: string): string {
  let out = text;
  for (const { pattern, replace } of REDACTIONS) {
    out = out.replace(pattern, replace);
  }
  return out;
}

/** Console arguments are anything at all; make them a line without throwing. */
function stringifyArg(arg: unknown): string {
  if (typeof arg === "string") return arg;
  if (arg instanceof Error) return `${arg.name}: ${arg.message}`;
  try {
    return JSON.stringify(arg) ?? String(arg);
  } catch {
    // Circular structures, getters that throw, DOM nodes.
    return String(arg);
  }
}

export function recordConsole(level: ConsoleLevel, args: unknown[]): void {
  const raw = args.map(stringifyArg).join(" ");
  const text = redactSensitive(raw).slice(0, MAX_LINE_CHARS);
  buffer.push({ at: new Date().toISOString(), level, text });
  // Ring, not a growing array: the oldest line is the least useful one.
  if (buffer.length > MAX_LINES) buffer.splice(0, buffer.length - MAX_LINES);
}

/**
 * Start capturing. Called once, as early as possible.
 *
 * The original console is still called, so devtools behave exactly as before —
 * this observes, it does not replace.
 */
export function installConsoleCapture(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;

  const levels: ConsoleLevel[] = ["log", "info", "warn", "error"];
  for (const level of levels) {
    const original = console[level].bind(console);
    console[level] = (...args: unknown[]) => {
      recordConsole(level, args);
      original(...args);
    };
  }

  window.addEventListener("error", (event) => {
    recordConsole("error", [
      `Uncaught ${event.message}`,
      `${event.filename}:${event.lineno}:${event.colno}`,
    ]);
  });

  // A rejected promise nobody caught is the most common silent failure in this
  // app — every API call is one.
  window.addEventListener("unhandledrejection", (event) => {
    recordConsole("error", ["Unhandled rejection", stringifyArg(event.reason)]);
  });
}

/**
 * A failed API call, recorded for the log.
 *
 * WITHOUT THIS THE LOG IS EMPTY. Measured 2026-09-13 and confirmed again
 * 2026-09-16: `web/src` contains ZERO `console.*` calls of its own. Capturing
 * the console alone would ship a field that arrives blank except for stray
 * warnings from the barcode library. The breadcrumbs are what give a bug report
 * something to say.
 *
 * STATUS AND PATH ONLY — NEVER THE RESPONSE BODY. `apiClient.ts:296-298`
 * attaches `status` and `body` to its Error as OWN ENUMERABLE properties, so
 * `JSON.stringify(err)` yields the whole server response; for order endpoints
 * that is customerName, customerPhone, customerEmail and deliveryAddress. The
 * server's own text is no safer — the exception filter returns Prisma messages
 * verbatim, complete with column and constraint names.
 *
 * The path is stripped of its query string for the same reason: ids and search
 * terms end up there.
 */
export function noteApiFailure(
  method: string,
  path: string,
  status: number,
): void {
  const safePath = path.split("?")[0];
  recordConsole("error", [`API ${method} ${safePath} failed with ${status}`]);
}

/** The tail, oldest first, as the report stores it. */
export function consoleTail(): string {
  return buffer
    .map((e) => `${e.at} [${e.level}] ${e.text}`)
    .join("\n");
}

/** Only for tests — the buffer is a module singleton by design. */
export function resetConsoleBuffer(): void {
  buffer.length = 0;
}
